import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { PROCESS_STEPS } from "@/data/site";
import { Reveal, SectionLabel } from "@/components/Reveal";

export const ProcessSection = () => (
  <section className="relative overflow-hidden bg-[#F7F9FC] py-20 md:py-28" data-testid="process-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <SectionLabel>Our Process</SectionLabel>
          <h2 className="mt-5 font-display text-3xl md:text-5xl font-light tracking-tight text-[#102A43] text-balance">
            A simple <span className="font-extrabold">step-by-step</span> approach
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[#486581]">
            From the first conversation through to installation and handover, we keep the process clear, professional and straightforward.
          </p>
        </Reveal>
      </div>

      {/* Desktop connected timeline */}
      <div className="mt-16 hidden lg:block">
        <div className="relative grid grid-cols-5 gap-5">
          <div className="absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-[#0B4F8A]/20 via-[#1F6FB2]/40 to-[#0B4F8A]/20" />
          {PROCESS_STEPS.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Wrench;
            return (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative flex flex-col items-start">
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B4F8A] text-white shadow-[0_10px_24px_-8px_rgba(11,79,138,0.6)]">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                    <span className="font-display text-sm font-bold text-[#1F6FB2]">STEP {s.n}</span>
                    <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-[#102A43]">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#486581]">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* Mobile / tablet vertical timeline */}
      <div className="mt-14 lg:hidden">
        <div className="relative space-y-6 pl-2">
          {PROCESS_STEPS.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Wrench;
            const last = i === PROCESS_STEPS.length - 1;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="relative flex gap-5"
              >
                <div className="flex flex-col items-center">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0B4F8A] text-white">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  {!last && <div className="mt-2 w-px flex-1 bg-[#1F6FB2]/30" />}
                </div>
                <div className="flex-1 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm mb-2">
                  <span className="font-display text-xs font-bold text-[#1F6FB2]">STEP {s.n}</span>
                  <h3 className="mt-1.5 font-display text-base font-semibold text-[#102A43]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#486581]">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);
